import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, X, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetHeader,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  storefrontApiRequest,
  formatMoney,
  shopifyImg,
  productImageAlt,
} from "@/lib/shopify";

interface PredictiveProduct {
  id: string;
  title: string;
  handle: string;
  featuredImage?: { url: string; altText: string | null } | null;
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
}

const PREDICTIVE_QUERY = `
  query Predictive($query: String!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    predictiveSearch(query: $query, limit: 6, types: PRODUCT) {
      products {
        id
        title
        handle
        featuredImage { url altText }
        priceRange { minVariantPrice { amount currencyCode } }
      }
    }
  }
`;

/**
 * Lightweight predictive search sheet driven by Shopify Storefront API.
 * Opens from the header, debounces the input, and lets shoppers jump
 * straight to a product page — reducing "can't find it" bounces.
 */
export function SearchSheet({ ariaLabel = "Etsi tuotteita" }: { ariaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState<PredictiveProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(id);
    }
    setQ("");
    setItems([]);
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await storefrontApiRequest(PREDICTIVE_QUERY, { query: term });
        if (cancelled) return;
        const products: PredictiveProduct[] = res?.data?.predictiveSearch?.products ?? [];
        setItems(products);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [q]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={ariaLabel}
          className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <Search className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="top" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">Etsi tuotteita</SheetTitle>
        </SheetHeader>
        <div className="mx-auto mt-4 max-w-2xl">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Esim. Pikler-kolmio, oppimistorni, IGLU…"
              className="h-12 w-full rounded-full border border-border bg-background pl-10 pr-10 text-base text-foreground outline-none ring-0 transition-colors focus:border-foreground"
              autoComplete="off"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Tyhjennä haku"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </label>

          <div className="mt-6 min-h-[120px]">
            {loading && (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {!loading && q.trim().length >= 2 && items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Ei tuloksia haulle "{q}". Kokeile toista sanaa tai{" "}
                <Link to="/kauppa" onClick={() => setOpen(false)} className="underline">
                  selaa koko mallistoa
                </Link>
                .
              </p>
            )}
            {!loading && items.length > 0 && (
              <ul className="divide-y divide-border">
                {items.map((p) => (
                  <li key={p.id}>
                    <Link
                      to="/product/$handle"
                      params={{ handle: p.handle }}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-4 py-3 transition-colors hover:bg-foreground/5"
                    >
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-surface ring-1 ring-border/60">
                        {p.featuredImage?.url && (
                          <img
                            src={shopifyImg(p.featuredImage.url, 120)}
                            alt={productImageAlt(p.featuredImage.altText, p.title)}
                            width={56}
                            height={56}
                            loading="lazy"
                            className="h-full w-full object-contain p-1"
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{p.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatMoney(
                            p.priceRange.minVariantPrice.amount,
                            p.priceRange.minVariantPrice.currencyCode,
                          )}
                        </p>
                      </div>
                      <span aria-hidden className="text-muted-foreground">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {!loading && q.trim().length < 2 && (
              <div>
                <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Suositut haut
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Pikler-kolmio", "Oppimistorni", "IGLU", "Vaaterekki", "Lattiapatja"].map(
                    (term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => setQ(term)}
                        className="rounded-full border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground"
                      >
                        {term}
                      </button>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}